import { useMemo } from 'react'

import { type SanityImageFragment } from '@data/sanity/queries/types/image'
import {
  type SanityProductGalleryPhoto,
  type SanityProductVariantFragment,
} from '@data/sanity/queries/types/product'
import { type FrameSettings } from '@frame-builder/lib/frame-viewer/settings'
import { type GalleryCarouselItem } from '@lib/gallery'
import {
  getProductGalleryItems,
  getProductGalleryPhotos,
} from '@lib/product/images'

import Gallery, { type GalleryProps } from '@blocks/gallery'

type ProductGalleryProps = Pick<
  GalleryProps,
  | 'hasArrows'
  | 'hasDots'
  | 'hasDrag'
  | 'hasCounter'
  | 'hasThumbnails'
  | 'preloadFirstImage'
> & {
  photosets: SanityProductGalleryPhoto[]
  frameSettings?: FrameSettings
  activeVariant?: SanityProductVariantFragment
  previewVariant?: SanityProductVariantFragment
}

const ProductGallery = ({
  photosets,
  frameSettings,
  activeVariant,
  previewVariant,
  hasArrows = false,
  hasDots = false,
  hasDrag = false,
  hasCounter = false,
  hasThumbnails = false,
  preloadFirstImage = false,
}: ProductGalleryProps) => {
  const items = useMemo(() => {
    let photos: SanityImageFragment[] = []

    // Get photos from preview variant
    if (previewVariant) {
      photos = getProductGalleryPhotos(photosets, previewVariant)
    }

    // Get photos from active variant or from product (non-specific variant)
    if (photos.length === 0) {
      photos = getProductGalleryPhotos(photosets, activeVariant)
    }

    // Get photos from first variant with any photos
    if (photos.length === 0) {
      photos =
        photosets.find(
          (photoset) => photoset?.photos && photoset.photos.length > 0,
        )?.photos ?? []
    }

    let items: GalleryCarouselItem[] = getProductGalleryItems(photos)

    // Add 3D preview as first item
    if (frameSettings) {
      items = [
        {
          id: `3d-preview-${activeVariant?.variantID}`,
          type: '3d-preview',
          frameSettings,
        },
        ...items,
      ]
    }

    return items
  }, [activeVariant, frameSettings, photosets, previewVariant])

  return (
    <Gallery
      items={items}
      hasArrows={hasArrows}
      hasDots={hasDots}
      hasDrag={hasDrag}
      hasCounter={hasCounter}
      hasThumbnails={hasThumbnails}
      preloadFirstImage={preloadFirstImage}
    />
  )
}

export default ProductGallery
